/**
 * In-memory handoff for "Edit in Contracts" deep links (SE-7737).
 * @remarks Why the URL is not enough. The Financials tab navigates in-app to
 * `/squad?playerId=…&playerName=…`, but the Squad tab screen never unmounts
 * (`app/(root)/(tabs)/_layout.tsx` sets no `unmountOnBlur`), so a second
 * navigation only changes params on an already-mounted screen. Two successive
 * clicks on the SAME player therefore produce byte-identical params: no value
 * a latch can compare changes, so the screen cannot tell "clicked again" from
 * "nothing happened". The clicking surface is the only place that knows a
 * fresh navigation just happened, so it hands the intent over directly — each
 * click records a request with a new token, and the Squad screen consumes each
 * token at most once. Full-reload arrivals and shared links still ride the URL
 * param path in `SquadScreenContainer`; this store only exists for in-app
 * clicks.
 *
 * Deliberately the same shape as
 * `features/player-kanban/utils/playerDeepLinkRequests` (SE-7145), which
 * solved the identical "ref latch on a screen that outlives the navigation"
 * defect on the pipeline board. Built on an Apollo reactive var — the repo's
 * pattern for module-level reactive state — so consumers subscribe with
 * `useReactiveVar` and the functional-immutability lint holds.
 * @module features/contracts/utils/contractDrawerDeepLinkRequests
 */

import { makeVar } from "@apollo/client";

import type { BreadcrumbEntry } from "@/providers/NavigationProvider";

/**
 * Path of the crumb that stands for "you are on Squad, having arrived from a
 * player's Financials tab" (SE-7738). The push side (the Financials tab) and
 * the read side (the Squad screen) share this one definition so there is a
 * single notion of the trail rather than two that can drift apart.
 */
export const CONTRACT_DRAWER_RETURN_CRUMB_PATH = "/squad";

/** Label of that crumb — the surface the user landed on. */
export const CONTRACT_DRAWER_RETURN_CRUMB_LABEL = "Contracts";

/** Route prefix every player-profile crumb path starts with. */
const PLAYER_PATH_PREFIX = "/players/";

/**
 * Whether the breadcrumb stack is the "player → Squad contracts" trail this
 * flow pushes, and therefore whether the Squad screen owes the user a way
 * back to that player.
 *
 * The check is deliberately narrow. Breadcrumbs outlive a navigation, and the
 * OPPOSITE direction (`ContractDrawerHeaderContainer`'s Profile link) leaves
 * the stack as `[Squad, Player]` — the same two surfaces in the other order.
 * Requiring Squad to be LAST and a player crumb immediately before it keeps
 * the affordance off every trail that merely passed through Squad.
 * @param breadcrumbs - The current breadcrumb stack.
 * @returns True when the trail ends at Squad having come from a player page.
 */
export function hasContractDrawerReturnTrail(
  breadcrumbs: readonly BreadcrumbEntry[]
): boolean {
  if (breadcrumbs.length < 2) return false;
  const last = breadcrumbs[breadcrumbs.length - 1];
  const previous = breadcrumbs[breadcrumbs.length - 2];
  return (
    last.path === CONTRACT_DRAWER_RETURN_CRUMB_PATH &&
    previous.path.startsWith(PLAYER_PATH_PREFIX)
  );
}

/** One click's worth of contract-drawer deep-link intent. */
export interface ContractDrawerDeepLinkRequest {
  /** The player whose contract drawer should open. */
  readonly playerId: string;
  /**
   * Display name to seed the drawer header with while the roster query
   * resolves. Empty string renders as "no subtitle".
   */
  readonly playerName: string;
  /** Monotonic per-click identity — the same player twice is two requests. */
  readonly token: number;
}

/** The latest unconsumed request, or `null`. */
export const contractDrawerDeepLinkRequestVar =
  makeVar<ContractDrawerDeepLinkRequest | null>(null);

/** The last token handed out, so consumed requests keep the sequence. */
const lastTokenVar = makeVar<number>(0);

/**
 * Record a click's deep-link intent, just before navigating to `/squad`.
 * @param target - The player the click asked for.
 * @param target.playerId - The player whose contract drawer should open.
 * @param target.playerName - Display name to seed the drawer header with.
 */
export function requestContractDrawerDeepLink(target: {
  playerId: string;
  playerName?: string | null;
}): void {
  const token = lastTokenVar() + 1;
  lastTokenVar(token);
  contractDrawerDeepLinkRequestVar({
    playerId: target.playerId,
    playerName: target.playerName ?? "",
    token,
  });
}

/**
 * Mark a request handled, clearing it so it can never replay — e.g. against a
 * later remount of the Squad screen. A stale token (a newer click already
 * replaced the request) is a no-op.
 * @param token - The token of the request that was applied.
 */
export function consumeContractDrawerDeepLinkRequest(token: number): void {
  if (contractDrawerDeepLinkRequestVar()?.token !== token) return;
  contractDrawerDeepLinkRequestVar(null);
}

/**
 * Test-only: drop any pending request and restart the token sequence so suites
 * cannot leak clicks into each other.
 */
export function resetContractDrawerDeepLinkRequestsForTests(): void {
  contractDrawerDeepLinkRequestVar(null);
  lastTokenVar(0);
}
